import type { Language } from '@eformulieren/engine'
import type { Projection } from '@eformulieren/model'
import { type AccessData, type AdviceOffer, type ShareStart, ShareDialog, ShareIcon } from '@eformulieren/renderer'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { type ReadAdvice, shareButtonLabel, shareOffer } from './sharing.js'
import { createShareClient } from './shareClient.js'

/**
 * De knop en de dialoog voor delen en advies vragen (R-64, R-65, R-66, R-66a, R-184, besluiten 1 tot
 * en met 7 van 30 oktober 2026, ontwerp 6.4, 7.4a en 9.2).
 *
 * **Wat de knop zegt** (`shareButtonLabel`). "Delen en advies vragen" aan de interne kant als de kijker
 * advies kan vragen (`kanVragen`), anders "Delen", en "Wie heeft toegang" voor wie niet mag delen (`magDelen` uit de projectie, R-64d, R-65d): het
 * overzicht van de eigen kant is voor elke deelnemer (R-66), en de pod filtert het op de kant uit het
 * token (R-66a).
 *
 * **Gestuurd door de pagina.** Of de dialoog openstaat en hoe, staat in `openWith` en wordt
 * veranderd met `onOpenWith`; de pagina houdt die toestand bij (`FormPage`). Zo kan ook een ander
 * deel van het formulier de dialoog openen: het adviespaneel en de tekstballon bij een vraag met
 * `{ rol: 'advies', anker }`, "Deze sectie delen" met `{ secties: [id] }`. Bekijk advies in de lijst
 * roept `onBekijkAdvies` aan; zonder die prop staat de knop er niet.
 *
 * **Wat er bij advies te kiezen is** komt van de pagina (`advies`, uit `useAdvice`): dezelfde lezing van
 * `GET /advies` als het adviespaneel en de ballonnen bij de vragen, zodat knop, dialoog en paneel het
 * eens zijn. Een storing daar is "kan niet vragen" en blokkeert de dialoog niet.
 *
 * De verzoeken zelf staan in `shareClient.ts`.
 */

export interface ShareButtonProps {
  readonly taal: Language
  readonly instantieId: string
  readonly projectie: Projection
  /** De koppen voor een verzoek: een token als er een is, anders niets (`headersFor`). */
  readonly headers: Readonly<Record<string, string>>
  /** De secties die nu verborgen zijn in het formulier (`onHiddenSections` van `FillForm`). */
  readonly verborgenSecties?: readonly string[]
  /** Open met deze start, of dicht als er niets staat. */
  readonly openWith: ShareStart | undefined
  readonly onOpenWith: (start: ShareStart | undefined) => void
  /** Een adviesverzoek in het adviespaneel tonen. */
  readonly onBekijkAdvies?: (adviesId: string) => void
  /**
   * Wat er bij advies te kiezen is en de verzoeken voor de lijst, van de pagina (`useAdvice`): dezelfde
   * lezing als het paneel, zodat knop, dialoog en paneel het eens zijn over `kanVragen`.
   */
  readonly advies: ReadAdvice | 'laden'
  /** Na een handeling in de dialoog: de pagina leest advies opnieuw. */
  readonly onAdviesGewijzigd?: () => Promise<void>
  /**
   * De pod gaf 401: de sessie is verlopen (`session.ts`). De dialoog gaat dicht, want de melding met
   * "Opnieuw inloggen" staat boven het formulier en een modale dialoog zou haar afdekken.
   */
  readonly onSessionExpired?: () => void
}

function logChain(chain: Readonly<Record<string, unknown>>): void {
  console.error('eformulieren: delen niet gelukt', chain)
}

export function ShareButton({
  taal: language,
  instantieId,
  projectie: projection,
  headers,
  verborgenSecties: hiddenSections,
  openWith,
  onOpenWith,
  onBekijkAdvies,
  onSessionExpired,
  advies: advice,
  onAdviesGewijzigd: onAdviceChanged,
}: ShareButtonProps): ReactNode {
  const [access, setAccess] = useState<AccessData | 'laden' | 'mislukt'>('laden')

  const sectionName = useCallback(
    (sectionId: string): string | undefined => projection.secties.find((section) => section.id === sectionId)?.titel[language],
    [projection, language],
  )
  // De pagina maakt de koppen en de callbacks bij elke render opnieuw; de client hangt aan wat ze
  // zeggen, anders haalt elke render de lijst opnieuw op.
  const headerKey = JSON.stringify(headers)
  const callbacks = useRef({ onOpenWith, onSessionExpired })
  callbacks.current = { onOpenWith, onSessionExpired }
  const client = useMemo(
    () =>
      createShareClient({
        instantieId,
        headers: JSON.parse(headerKey) as Readonly<Record<string, string>>,
        taal: language,
        sectionName,
        fetch: (input, init) => globalThis.fetch(input, init),
        onSessionExpired: () => {
          callbacks.current.onOpenWith(undefined)
          callbacks.current.onSessionExpired?.()
        },
        log: logChain,
      }),
    [instantieId, headerKey, language, sectionName],
  )

  const adviceRef = useRef(advice)
  adviceRef.current = advice
  /** De lijst, met de adviesverzoeken erbij aan de interne kant: uit het overzicht, of uit `GET /advies`. */
  const loadAccess = useCallback(async (): Promise<void> => {
    const overview = await client.overview()
    if (overview === 'mislukt') {
      setAccess('mislukt')
      return
    }
    const known = adviceRef.current === 'laden' ? [] : adviceRef.current.verzoeken
    const requests = projection.kant !== 'intern' ? [] : overview.advies.length > 0 ? overview.advies : known
    setAccess({ delingen: overview.delingen, toekenningen: overview.toekenningen, openingen: overview.openingen, advies: requests })
  }, [client, projection.kant])

  const open = openWith !== undefined
  useEffect(() => {
    if (!open) return
    setAccess('laden')
    void loadAccess()
  }, [open, loadAccess])

  const offer: AdviceOffer | undefined = advice !== 'laden' && advice.kanVragen ? { buitenNwo: advice.buitenNwo } : undefined
  const sections = useMemo(
    () =>
      shareOffer(projection, language, {
        verborgen: new Set(hiddenSections ?? []),
        uitgesloten: advice === 'laden' ? [] : advice.uitgesloten,
      }),
    [projection, language, hiddenSections, advice],
  )
  /** Na een handeling de lijst opnieuw: wat er staat is wat de pod ervan maakte. */
  const after = useCallback(
    async <T extends { readonly soort: string }>(outcome: Promise<T>): Promise<T> => {
      const done = await outcome
      if (done.soort !== 'geweigerd') {
        await onAdviceChanged?.()
        await loadAccess()
      }
      return done
    },
    [loadAccess, onAdviceChanged],
  )

  const label = shareButtonLabel(projection, advice !== 'laden' && advice.kanVragen, language)

  return (
    <>
      <button
        type="button"
        className="ef-actieknop"
        data-soort="secundair"
        data-proef="delen"
        aria-haspopup="dialog"
        onClick={() => {
          onOpenWith(projection.magDelen ? {} : { tab: 'toegang' })
        }}
      >
        {/*
          Een icoon voor het label, zoals Bronnen, Opmerkingen en Geschiedenis ernaast (besluit van 2 november
          2026). Op een telefoon verbergt de stylesheet het woord alleen visueel (`schil.css`): de naam
          voor de schermlezer blijft het label, en het icoon telt daarin niet mee.
        */}
        <ShareIcon aria-hidden="true" />
        <span>{label}</span>
      </button>
      {/*
        Bij advies pas als bekend is of de kijker kan vragen: de dialoog kiest zijn rol bij het openen.

        In `body` en niet naast de knop. Tot 2 november 2026 stond de knop op een smal scherm achter
        "Meer" in de kopbalk, en dat deel is `display: none` zolang het dicht is; een modale dialoog
        onder een verborgen voorouder toont niets. Sinds die dag staat hij in de werkbalk van het
        formulier (eisenboek 12.47), maar de dialoog blijft in `body`, los van waar de knop staat. De tekstballon bij een vraag en "Advies vragen" in het paneel openen
        de dialoog zonder die knop, en deden op een telefoon dus niets (gevonden in
        `e2e/advice-on-phone.spec.ts`, besluit 10 van 30 oktober 2026).
      */}
      {open && advice !== 'laden' ? createPortal(
        <ShareDialog
          taal={language}
          kant={projection.kant}
          secties={sections}
          canInvite={projection.magDelen}
          {...(offer === undefined ? {} : { advies: offer })}
          start={openWith}
          toegang={access}
          sectionName={sectionName}
          onInvite={(request) => after(client.invite(request))}
          onWijzigen={(id, rights) => after(client.change(id, rights))}
          onIntrekken={(id) => after(client.withdraw(id))}
          onOpnieuw={(line) => after(client.inviteAgain(line))}
          {...(projection.kant === 'intern'
            ? {
                onHerinneren: (id: string) => after(client.remind(id)),
                onAdviesIntrekken: (id: string) => after(client.withdrawAdvice(id)),
              }
            : {})}
          {...(onBekijkAdvies === undefined ? {} : { onBekijkAdvies })}
          onClose={() => {
            onOpenWith(undefined)
          }}
          // Opnieuw lezen zonder eerst "laden": de lijst blijft staan tot het nieuwe antwoord er is.
          onShowAccess={() => void loadAccess()}
        />,
        document.body,
      ) : null}
    </>
  )
}
